import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Alert, Button, useMediaQuery } from '@mui/material';
import { requirementsApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePref } from '../../services/prefs';
import IdChip from '../common/IdChip';
import ReviewHeader from './review/ReviewHeader';
import ItemBlock from './review/ItemBlock';
import ItemChildrenList from './review/ItemChildrenList';
import CommentThread from './review/CommentThread';
import ReviewFooter from './review/ReviewFooter';
import ApprovalTrailVerdict from './review/ApprovalTrailVerdict';
import { collectVisibleIds, computeOpenCounts, computeRollup } from './review/reviewUtils';
import './review/review.css';

const ATTRS_KEY = 'rmdv.breakdown.showAttributes';

function readShowAttributes() {
  try {
    return localStorage.getItem(ATTRS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * BreakdownTab — the PR-style review-feedback view for a container's subtree
 * (#342). Owns the `GET /items/{id}/breakdown` fetch, loading/error states,
 * and renders ReviewHeader + the recursive ItemBlock tree + ReviewFooter.
 *
 * LAZY + CACHED exactly like DocumentTab: this component is only mounted
 * while its TabPanel is active, and the cache is owned by the PARENT
 * (ItemDetails, via getCachedReview/setCachedReview) so switching tabs away
 * and back serves from cache instead of refetching (a TabPanel unmounts its
 * inactive children, so a component-local cache would be discarded).
 */
const BreakdownTab = ({
  itemId, getCachedReview, setCachedReview, onNavigateToItem, reviewChangeCount,
}) => {
  const auth = useAuth();
  const user = auth && auth.user;
  const isMobile = useMediaQuery('(max-width:640px)');

  const [payload, setPayload] = useState(() => (getCachedReview ? getCachedReview(itemId) : undefined));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showAttributes, setShowAttributes] = useState(readShowAttributes);
  const [expandedThreads, setExpandedThreads] = useState(() => new Set());
  const [openComposer, setOpenComposer] = useState(null); // { kind: 'item'|'overall', id } | null
  const composerRef = useRef(null);
  // Item ids whose approval write is in flight (owner ask, 2026-07-28: spinner on click
  // while awaiting the response). A SET, not a boolean: one breakdown renders a whole
  // subtree of pills and only the clicked one may spin.
  const [approvingIds, setApprovingIds] = useState(() => new Set());
  // #174: the friendly-id/raw-UUID preference. Resolved once here and threaded down
  // rather than subscribed to at every depth of ItemBlock's recursion.
  const showHumanIds = usePref('showHumanIds');

  // #980 — the client-only sequence guard against a three-actor stale write.
  //
  // A mutation handler that merges its OWN response into the payload
  // (`{...c, ...updated}`) is correct against a single writer and wrong once a
  // SECOND reviewer has written the same row:
  //
  //   1. Reviewer A sends PATCH `resolve` on CMT-3.
  //   2. Reviewer B sends PATCH `close` on the same comment; the server applies
  //      it second, so `closed` is the true state.
  //   3. The forced GET (the `reviewChangeCount` effect below) returns
  //      `status: 'closed'` and the mirror renders it correctly.
  //   4. A's PATCH response lands LAST and spreads A's older row over B's newer
  //      one. The mirror shows `addressed` where the server holds `closed`.
  //
  // Today's PATCH response carries nothing that says which of two responses is
  // newer, so the ordering is reconstructed locally: one monotonic counter, a
  // stamp taken when each mutation is ISSUED, and the counter value recorded
  // whenever a server payload is APPLIED. A response whose stamp predates the
  // last applied payload is describing rows that payload has already re-read
  // from the server, so it is dropped.
  //
  // What this deliberately does NOT do: order two PATCH responses against each
  // other with no refetch between them. That needs a server-supplied version or
  // `updated_at` on the comment, which changes the API contract — recorded on
  // #980 as the better fix if the no-refetch ordering turns up in practice.
  //
  // #992 CORRECTION — a payload is stamped when its GET is ISSUED, not when it is
  // applied. The rule above rests on "that payload has already re-read from the
  // server", and that premise is false whenever the GET went out BEFORE the
  // mutation: such a payload cannot contain the write, yet an apply-time stamp
  // still made it look newer, and the mutation's own authoritative response was
  // dropped. The `reviewChangeCount` effect below fires that GET from a 10-second
  // poll with no user action, so it can already be in flight at the moment a
  // button is clicked — the user then sees the PRE-CLICK status until the next
  // poll re-reads the row. Reproduced deterministically in
  // `__tests__/BreakdownTab.test.js` ("a refetch issued BEFORE the write"), which
  // fails on apply-time stamping and passes on issue-time.
  //
  // The cost of a false drop was called bounded: the next forced refetch (the same
  // 10s review-activity poll that opens the window) re-reads the row from the
  // server, and every field these handlers write — `status`, `resolution`,
  // `escalated_cr_id` — is carried on the breakdown payload's own comment rows.
  // Bounded is not free. Ten seconds of a button that visibly did nothing is the
  // #650 symptom the try/catch below was added to end, arriving by another route.
  const seqRef = useRef(0);
  const lastAppliedPayloadSeqRef = useRef(0);
  const nextSeq = useCallback(() => {
    seqRef.current += 1;
    return seqRef.current;
  }, []);
  // Strict `<`: a mutation stamped AFTER the last applied payload is the newer
  // writer and must be merged, which is what keeps the guard from dropping
  // everything.
  const supersededByRefetch = useCallback((stamp) => stamp < lastAppliedPayloadSeqRef.current, []);

  const load = useCallback(async (id, { force = false } = {}) => {
    if (!id) return;
    const cached = getCachedReview ? getCachedReview(id) : undefined;
    if (!force && cached !== undefined) {
      setPayload(cached);
      setError(null);
      return;
    }
    // #992: the payload's age is when its request went OUT. Stamping it here rather
    // than after the await is what makes it comparable with a mutation's stamp,
    // which is also taken at issue time.
    const issued = nextSeq();
    setLoading(true);
    setError(null);
    try {
      const data = await requirementsApi.getReview(id);
      // #992: two GETs can be in flight at once (an item switch during a poll's
      // forced refresh), and the later-issued one can land first. Applying this
      // older read over it would put a stale payload on screen with nothing left
      // to correct it until the next poll, so an out-of-order response is dropped.
      if (issued < lastAppliedPayloadSeqRef.current) return;
      // #980: this payload is the newest read of these rows the client holds, so a
      // mutation issued BEFORE the GET went out is describing rows this payload has
      // already re-read. The cache short-circuit above is deliberately not stamped:
      // a cached payload is not a fresh server read.
      lastAppliedPayloadSeqRef.current = issued;
      if (setCachedReview) setCachedReview(id, data);
      setPayload(data);
    } catch (err) {
      setError(`Could not load review: ${err.message}`);
      // DO NOT discard what is already on screen (owner, 2026-07-28: a "Could not load
      // review: Network Error" appeared "in the item view breakdown tab where I was
      // working removing my view").
      //
      // This used to `setPayload(null)`, which drops the component into the
      // `error && !payload` branch below — the entire review the user was reading is
      // replaced by an error card. The trigger is not necessarily a user action: this
      // same `load(..., {force: true})` runs automatically whenever the review-activity
      // probe reports a change, so ONE failed background refresh could wipe a review
      // mid-edit. A refresh failing is not a reason to destroy the last good answer.
      //
      // Keeping it means a failed refresh degrades to "stale content + a visible error
      // banner with Retry" instead of "no content". The banner is rendered above the
      // payload, so the staleness is never silent. The `!payload` branch still handles
      // the genuine first-load failure, where there is nothing to preserve.
    } finally {
      setLoading(false);
    }
  }, [getCachedReview, setCachedReview, nextSeq]);

  // #669: the tamper-evidence verdict for THIS item's approval chain.
  //
  // Scoped to the focused item deliberately, not to every node in the breakdown
  // subtree. `verify_approval_trail` is one query plus a hash walk — measured at a
  // 37.9ms median in-process (7 samples, cold discarded) — so per item it is
  // cheap, but the breakdown renders a whole SUBTREE and N verifies is linear:
  // 20 items measured 758.9ms. The reader opened THIS item, so this item's chain
  // is the one whose verdict they are owed; a child's verdict appears when they
  // open the child. Stated so the limit reads as a decision rather than an
  // oversight. Covering the subtree properly needs a batched
  // `get_approval_events` (`item_uuid.in_(ids)`, one query for N items), not N
  // calls to verify.
  //
  // `undefined` until it settles, so nothing paints while it is in flight.
  const [trailVerdict, setTrailVerdict] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    setTrailVerdict(undefined);
    if (!itemId) return undefined;
    // `Promise.resolve(...)` rather than assuming a thenable: the verdict is
    // ADVISORY and must never be able to take down the review tab. A client that
    // returns undefined (or is stubbed out) then leaves the verdict unsettled,
    // which renders nothing — the same as "still loading" — instead of throwing.
    Promise.resolve(requirementsApi.verifyApprovalTrail(itemId)).then((v) => {
      // The client itself never rejects — it resolves `{ok: null}` on any failure
      // — so a failed verify lands as an explicit INDETERMINATE, never as silence
      // that a reader would take for "verified".
      if (!cancelled) setTrailVerdict(v);
    }).catch(() => { /* advisory: never break the tab */ });
    return () => { cancelled = true; };
    // Re-verified on the same signal that refetches the review, so a tamper that
    // lands while the tab is open is not hidden behind a stale verdict.
  }, [itemId, reviewChangeCount]);

  // Fetch when this tab is mounted/active for an item — the parent only
  // mounts BreakdownTab while the Breakdown tab is selected (LAZY, mirrors
  // DocumentTab).
  useEffect(() => {
    load(itemId);
    // Switching items: any open composer/expanded state from the PREVIOUS
    // item no longer applies.
    setOpenComposer(null);
    setExpandedThreads(new Set());
    setFilter('all');
    // `load` is safe to list: it is a useCallback over `getCachedReview`/`setCachedReview`,
    // and every mount site supplies stable values for those — ItemDetails passes a
    // `useCallback([], …)` pair over a ref-held cache; ProjectDetails and DashboardHome
    // pass neither (undefined). So this still runs once per item, not once per render.
  }, [itemId, load]);

  // N-21 / REQ-6133 (#609): live refresh — re-fetch (bypassing the cache) when the
  // PARENT reports another reviewer changed something in this item's project.
  //
  // This is the wiring the acceptance criterion actually names ("an open
  // Breakdown"), landed separately from the tree's own wiring because this
  // component has no project id in scope and no ProjectContext to derive one
  // from (recorded on #609 after a first attempt at `useReviewActivity(activeProject)`
  // in here directly was reverted — that hook needs a project id, and this
  // component never has one). Each of BreakdownTab's THREE mount sites
  // (ItemDetails.js, ProjectDetails.js, DashboardHome.jsx) resolves its OWN
  // project scope — differently, because each already has a different source
  // for it — and hands the resulting `changeCount` down as this prop, so the
  // hook's contract itself needed no change.
  //
  // `reviewChangeCount` starts a render behind `itemId` on a genuine item switch
  // (props update together), so the ref seeds to whatever count is already
  // current on mount — only a change OBSERVED WHILE MOUNTED forces a refetch;
  // the initial load above already covers the mount/item-switch case via cache.
  const seenReviewChangeRef = useRef(reviewChangeCount);
  useEffect(() => {
    if (reviewChangeCount === undefined) return;
    if (seenReviewChangeRef.current === reviewChangeCount) return;
    seenReviewChangeRef.current = reviewChangeCount;
    load(itemId, { force: true });
    // `itemId`/`load` are listed rather than suppressed: the `seenReviewChangeRef` guard
    // above makes every re-entry for an UNCHANGED count a no-op, so an item switch (which
    // carries the same count — see the note above) still cannot trigger a forced refetch.
    // Only an observed change in the count gets past the guard, which is the intent.
  }, [reviewChangeCount, itemId, load]);

  const toggleAttributes = useCallback(() => {
    setShowAttributes((prev) => {
      const next = !prev;
      try { localStorage.setItem(ATTRS_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const toggleThread = useCallback((id) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const mutatePayload = useCallback((updater) => {
    setPayload((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      if (setCachedReview) setCachedReview(itemId, next);
      return next;
    });
  }, [setCachedReview, itemId]);

  // `extra` carries the text the backend REQUIRES for two transitions: F-06 / REQ-6096
  // (#603) `reason` on a rejection, and F-08 / REQ-6098 `justification` when the
  // approval supersedes another reviewer's open rejection. ItemBlock collects it via
  // ApprovalReasonDialog BEFORE calling, so the write is never attempted without it —
  // the previous reasonless PUT was refused with a 400 and the pill just didn't move.
  const handleApprove = useCallback(async (targetId, nextState, extra) => {
    // Mark in flight BEFORE awaiting, so the pill spins on the click rather than on the
    // response — the whole point of the ask is the window between the two.
    setApprovingIds((prev) => new Set(prev).add(targetId));
    try {
      // Only pass the third argument when there is one — an explicit `undefined`
      // is noise on the call and would make every existing 2-arg assertion a lie.
      const result = extra
        ? await requirementsApi.setApproval(targetId, nextState, extra)
        : await requirementsApi.setApproval(targetId, nextState);
      mutatePayload((prev) => {
        const approvals = { ...prev.approvals };
        if (nextState === null || (result && result.cleared)) {
          approvals[targetId] = [];
        } else {
          const reviewer = (result && result.reviewer) || (user && user.id);
          const list = (approvals[targetId] || []).filter((a) => a.reviewer !== reviewer);
          // A just-set approval is fresh by construction — the server recomputed
          // the hash from the item's CURRENT state. The PUT /approval response
          // does NOT carry the computed `stale` flag (that's only in GET
          // /breakdown), so stamp it false here or a re-approve of a STALE item
          // would keep showing "Approved · stale" until a refetch.
          approvals[targetId] = [...list, result ? { ...result, stale: false } : null].filter(Boolean);
        }
        return { ...prev, approvals };
      });
    } catch (err) {
      setError(`Could not update approval: ${err.message}`);
    } finally {
      // `finally`, not the success path: a pill left spinning forever after a failed
      // write is a worse outcome than no spinner at all — the reviewer cannot retry, and
      // the control gives no hint that anything went wrong. The error banner reports the
      // failure; this just re-enables the button.
      setApprovingIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    }
  }, [mutatePayload, user]);

  // F-60 / REQ-6118 (#606, #629): the admin-override write. Unlike handleApprove,
  // there is no returned "current" row to merge in — an override REMOVES the target
  // reviewer's decision outright (the item reads `pending` again unless another
  // reviewer's row remains), so the local mirror just drops that reviewer's row from
  // the cached approvals — a refetch of the History tab (separate fetch, not cached
  // here) is what shows the `overridden` event itself.
  const handleOverride = useCallback(async (targetId, reviewer, reason) => {
    // #980: the issue's body clears this handler alongside handleApprove, on the
    // reasoning that both key on `reviewer` and one reviewer cannot race their
    // own row. handleApprove is safe for that reason. This one is not, because an
    // override names ANOTHER reviewer, and that reviewer can record a new decision
    // as soon as the override lands. The filter below then deletes a row the
    // server still holds — the same stale write reaching a different field.
    const stamp = nextSeq();
    try {
      await requirementsApi.overrideApproval(targetId, reviewer, reason);
      if (supersededByRefetch(stamp)) return;
      mutatePayload((prev) => {
        const approvals = { ...prev.approvals };
        approvals[targetId] = (approvals[targetId] || []).filter((a) => a.reviewer !== reviewer);
        return { ...prev, approvals };
      });
    } catch (err) {
      setError(`Could not override approval: ${err.message}`);
    }
  }, [mutatePayload, nextSeq, supersededByRefetch]);

  // #978: REPLACE-OR-APPEND on `comment_id`, never a blind append.
  //
  // This used to be `[...(threads[targetId] || []), comment]`, which added a
  // second copy of a comment the local payload already held. The window is the
  // one between `requirementsApi.addComment` going out and this updater running:
  // the `reviewChangeCount` effect above calls `load(itemId, {force: true})`, and
  // a refetch that resolves inside that window returns a payload the server built
  // AFTER the POST landed — so it already carries this comment. Appending then
  // produced two cards with one `comment_id`, one `id="comment-<id>"` and one
  // React key, which React itself reported as "two children with the same key".
  //
  // A real user reaches it with two reviewers in one project. `reviewActivity`
  // polls every 10s (utils/pollSchedule.js) and its count is SERVER-derived, so
  // the OTHER reviewer's write forces this reviewer's refetch while this
  // reviewer's comment is in flight. Playwright caught it on
  // `approval-587-comment-lifecycle.spec.ts:153` — one POST, two rendered cards.
  //
  // The server owns comment identity and returns it on the response, so the
  // response is authoritative for the row it names: merge it over whatever the
  // refetch already placed there, and append only when the row is genuinely new.
  // handlePatchComment/handleDelete/handleApprove/handleOverride/handleEscalate
  // below already key their updates this way, which is why the add path was the
  // only one that could duplicate.
  const handleAddComment = useCallback(async (targetId, body, opts = {}) => {
    const comment = await requirementsApi.addComment(targetId, body, opts);
    mutatePayload((prev) => {
      const threads = { ...prev.threads };
      const list = threads[targetId] || [];
      const newId = comment && comment.comment_id;
      // No id on the response leaves nothing to key on, so the old append is the
      // only behaviour left. De-duping on `undefined` would be worse than a
      // duplicate: it would collapse every unidentified row onto one.
      const at = newId ? list.findIndex((c) => c && c.comment_id === newId) : -1;
      threads[targetId] = at === -1
        ? [...list, comment]
        : list.map((c, i) => (i === at ? { ...c, ...comment } : c));
      return { ...prev, threads };
    });
    // Saving a new comment leaves that item's thread expanded (spec §Comments).
    setExpandedThreads((prev) => new Set(prev).add(targetId));
    return comment;
  }, [mutatePayload]);

  // #650: a rejected patch (409 IllegalTransition — e.g. the reopen 409 this issue was
  // filed about, before the backend transition was widened; 403 NotRaiser; 400
  // ValueError for a missing wont_fix note) used to be an unhandled promise rejection —
  // no error text, no state change; the button just appeared to do nothing. Matches the
  // try/catch + setError pattern its siblings handleDelete/handleEscalate already use
  // below, preferring the FastAPI `detail` string (handleDelete's pattern) over the
  // bare `err.message` (handleEscalate's weaker one).
  const handlePatchComment = useCallback(async (targetId, commentId, action, opts = {}) => {
    // #980: stamped BEFORE the request goes out — the window this guard closes
    // opens the moment the PATCH leaves, not when its response arrives.
    const stamp = nextSeq();
    try {
      const updated = await requirementsApi.patchComment(commentId, action, opts);
      // #980: a payload was applied after this PATCH was issued, so it already
      // holds a newer read of this comment — possibly ANOTHER reviewer's write.
      // `{...c, ...updated}` would spread this client's older row back over it.
      if (supersededByRefetch(stamp)) return;
      mutatePayload((prev) => {
        const threads = { ...prev.threads };
        threads[targetId] = (threads[targetId] || []).map((c) => (
          c.comment_id === commentId ? { ...c, ...updated } : c
        ));
        return { ...prev, threads };
      });
    } catch (err) {
      setError(err?.response?.data?.detail || `Could not ${action} comment: ${err.message}`);
    }
  }, [mutatePayload, nextSeq, supersededByRefetch]);

  const handleCompleteReview = useCallback(async (body) => {
    if (!body || !payload || !payload.subtree) return;
    await handleAddComment(payload.subtree.id, body, { kind: 'overall' });
  }, [handleAddComment, payload]);

  // Phase 3 — a threaded reply is just a comment with parent_comment_id; reuse
  // the add path so the thread updates + stays expanded.
  const handleReply = useCallback(async (targetId, parentCommentId, body) => {
    await handleAddComment(targetId, body, { parentCommentId });
  }, [handleAddComment]);

  // Delete a comment (raiser-only, open+unreplied — server enforces). Remove it
  // from the item's thread on success.
  const handleDelete = useCallback(async (targetId, commentId) => {
    try {
      await requirementsApi.deleteComment(commentId);
      mutatePayload((prev) => {
        const threads = { ...prev.threads };
        threads[targetId] = (threads[targetId] || []).filter((c) => c.comment_id !== commentId);
        return { ...prev, threads };
      });
    } catch (err) {
      setError(err?.response?.data?.detail || `Could not delete comment: ${err.message}`);
    }
  }, [mutatePayload]);

  // Phase 3 — escalate a comment into a change_request; stamp the returned CR
  // id back onto the comment in the payload so the thread shows "Escalated → CR".
  const handleEscalate = useCallback(async (targetId, commentId, fields) => {
    // #980: same window as handlePatchComment, reached through `escalated_cr_id`.
    const stamp = nextSeq();
    try {
      const res = await requirementsApi.escalateComment(commentId, fields);
      const crId = res && res.change_request && res.change_request.id;
      // #980: an applied payload already carries the server's own
      // `escalated_cr_id` for this comment (`get_threads_for_items` returns the
      // whole row), so dropping a superseded response loses nothing the next
      // refetch cannot restore.
      if (supersededByRefetch(stamp)) return;
      mutatePayload((prev) => {
        const threads = { ...prev.threads };
        threads[targetId] = (threads[targetId] || []).map((c) => (
          c.comment_id === commentId ? { ...c, escalated_cr_id: crId } : c
        ));
        return { ...prev, threads };
      });
    } catch (err) {
      setError(`Could not escalate comment: ${err.message}`);
    }
  }, [mutatePayload, nextSeq, supersededByRefetch]);

  const openItemComposer = useCallback((id) => setOpenComposer({ kind: 'item', id }), []);
  const closeComposer = useCallback(() => setOpenComposer(null), []);

  const threads = useMemo(() => (payload && payload.threads) || {}, [payload]);
  const approvals = useMemo(() => (payload && payload.approvals) || {}, [payload]);
  const openCountByItem = useMemo(() => computeOpenCounts(threads), [threads]);

  const visibleIds = useMemo(() => {
    if (!payload || !payload.subtree) return new Set();
    return collectVisibleIds(payload.subtree.children || [], filter, approvals, openCountByItem);
  }, [payload, filter, approvals, openCountByItem]);

  // Recomputed locally (not the server's one-shot `rollup` field) so the
  // header stays live after any approve/comment mutation without a refetch —
  // see reviewUtils.computeRollup's doc comment (spec Edge case 12).
  const rollup = useMemo(
    () => computeRollup(payload && payload.subtree, approvals, threads),
    [payload, approvals, threads]
  );

  // Mobile: while ANY composer is open, collapse the header + footer and keep
  // the composer in view above the on-screen keyboard (spec §Mobile).
  const composing = isMobile && !!openComposer;
  useEffect(() => {
    if (!composing || typeof window === 'undefined' || !window.visualViewport) return undefined;
    const handler = () => composerRef.current?.scrollIntoView?.({ block: 'center' });
    window.visualViewport.addEventListener('resize', handler);
    return () => window.visualViewport.removeEventListener('resize', handler);
  }, [composing]);

  // Deep-link from a "new comment" notification (#360): the URL carries a
  // `#comment-<id>` hash. Once the payload (threads) is loaded, expand the
  // thread that holds that comment, scroll it into view (smooth) and briefly
  // highlight it. Best-effort — a hash targeting a comment outside this subtree
  // is left untouched.
  const deepLinkedRef = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined' || !payload) return;
    const m = (window.location.hash || '').match(/^#comment-(.+)$/);
    if (!m) return;
    const commentId = m[1];
    if (deepLinkedRef.current === commentId) return;
    let ownerId = null;
    Object.entries(threads).forEach(([id, list]) => {
      if ((list || []).some((c) => c && c.comment_id === commentId)) ownerId = id;
    });
    if (!ownerId) return;
    deepLinkedRef.current = commentId;
    setExpandedThreads((prev) => new Set(prev).add(ownerId));
    // Let the (now-expanded) thread render before scrolling/highlighting.
    const t = setTimeout(() => {
      const el = document.getElementById(`comment-${commentId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const prevBg = el.style.backgroundColor;
      el.style.transition = 'background-color 0.6s ease';
      el.style.backgroundColor = 'rgba(255, 214, 0, 0.35)';
      setTimeout(() => { el.style.backgroundColor = prevBg; }, 1600);
    }, 80);
    return () => clearTimeout(t);
  }, [payload, threads]);

  if (loading && !payload) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }} data-testid="breakdown-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !payload) {
    return (
      <Box sx={{ mt: 1 }}>
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" onClick={() => load(itemId, { force: true })}>
              Retry
            </Button>
          )}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!payload || !payload.subtree) {
    return <div data-testid="breakdown-empty" className="t-body muted3" style={{ marginTop: 8 }}>No breakdown available for this container.</div>;
  }

  const { subtree } = payload;
  const rootThread = threads[subtree.id] || [];
  const topLevelChildren = (subtree.children || []).filter((c) => visibleIds.has(c.id));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="breakdown-tab">
      {/* #650: a mutation failure AFTER the breakdown has loaded (patch/delete/
          escalate/approve/override — every handler above calls `setError` in its
          catch) used to set this same `error` state with nowhere to render it: the
          Alert above only shows while `!payload`, which is never true once the tree
          has loaded. That made every one of those catches cosmetic — the state
          updated, but the user saw nothing, which is the actual mechanism behind
          "silently" in this issue's title. Surfaced here so a rejected action (a 409
          IllegalTransition, 403 NotRaiser, or any other) is visible instead of a
          no-op-looking button. */}
      {/* A LOAD failure now lands here too rather than replacing the view (see the
          catch in `load`), so the banner carries a Retry — otherwise a failed background
          refresh would leave stale content with no way to ask for it again short of
          switching tabs. */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}
              action={(
                <Button color="inherit" size="small"
                        onClick={() => { setError(null); load(itemId, { force: true }); }}>
                  Retry
                </Button>
              )}
              data-testid="breakdown-mutation-error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {!composing && (
        <ReviewHeader
          rollup={rollup}
          filter={filter}
          onFilterChange={setFilter}
          showAttributes={showAttributes}
          onToggleAttributes={toggleAttributes}
        />
      )}
      {/* N-20 / REQ-6132 (#596): the server-side breadth bound is signalled, never
          silent (`GET /items/{id}/breakdown`'s `truncated`/`truncation` — see
          comments_api.py). A caller-visible banner is the frontend half of that
          requirement — without this the payload could be a bounded PAGE and the
          Breakdown tab would still present it as a complete subtree. */}
      {!composing && payload.truncated && (
        <Alert severity="warning" data-testid="breakdown-truncated-banner" sx={{ mb: 1 }}>
          {`Showing ${payload.truncation ? payload.truncation.returned : 'some'} of this `
            + `container's items${payload.truncation ? ` (limit ${payload.truncation.limit})` : ''} `
            + '— not every item is displayed. Narrow your view to see the rest.'}
        </Alert>
      )}
      {/* #669: the tamper verdict sits ABOVE the review box, so a reader who opens
          this item meets it before they read any approval — "visible without
          anyone knowing to look" is the acceptance criterion, and something below
          the fold satisfies the letter of that and not the point. Renders nothing
          at all when the chain is intact. */}
      <ApprovalTrailVerdict verdict={trailVerdict} itemId={itemId} />
      {/* Overall-review box at the TOP (moved from the bottom per #342 review —
          "otherwise it's not clear it's even there to be used"). */}
      {!composing && (
        <ReviewFooter rootTitle={subtree.title} onComplete={handleCompleteReview} placement="top" />
      )}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }} className="bdwrap">
        {rootThread.length > 0 && (
          // Mock's root-feedback block (docs/design/specimen/review.html
          // `renderBreakdown()`'s ROOT.comments branch, #450): a lightweight
          // `.bditem d1` header (muted title + id chip), not a full ItemBlock row —
          // the root itself has no approve pill/description here, just its thread.
          <div className="bditem d1" data-testid="root-feedback-strip">
            <div className="bdhead">
              <span className="htitle" style={{ fontSize: 13, color: 'var(--text-2)' }}>
                {`Feedback on ${subtree.title}`}
              </span>
              <IdChip id={subtree.id} />
            </div>
            <CommentThread
              comments={rootThread}
              currentUserId={user && user.id}
              onAction={(commentId, action, opts) => handlePatchComment(subtree.id, commentId, action, opts)}
              onReply={(parentId, body) => handleReply(subtree.id, parentId, body)}
              onEscalate={(commentId, fields) => handleEscalate(subtree.id, commentId, fields)}
              onDelete={(commentId) => handleDelete(subtree.id, commentId)}
            />
          </div>
        )}
        {/* N-20 / REQ-6132 (#596): cap how many top-level rows mount at once —
            see ItemChildrenList's doc comment. Keyed/reset on `itemId` + `filter`
            so switching containers or filters starts from the first page again,
            same as the expandedThreads/composer reset above. */}
        <ItemChildrenList
          items={topLevelChildren}
          resetKey={`${itemId}:${filter}`}
          testIdSuffix="root"
          renderItem={(child) => (
            <ItemBlock
              key={child.id}
              node={child}
              depth={1}
              visibleIds={visibleIds}
              approvals={approvals}
              threads={threads}
              expandedThreads={expandedThreads}
              onToggleThread={toggleThread}
              showAttributes={showAttributes}
              currentUserId={user && user.id}
              openComposerId={openComposer && openComposer.kind === 'item' ? openComposer.id : null}
              onOpenComposer={openItemComposer}
              onCloseComposer={closeComposer}
              onApprove={handleApprove}
              approvingIds={approvingIds}
              showHumanIds={showHumanIds}
              onOverrideApproval={handleOverride}
              onAddComment={handleAddComment}
              onPatchComment={handlePatchComment}
              onReply={handleReply}
              onEscalate={handleEscalate}
              onDelete={handleDelete}
              onNavigateToItem={onNavigateToItem}
              composerRef={composerRef}
            />
          )}
        />
      </Box>
    </Box>
  );
};

BreakdownTab.propTypes = {
  itemId: PropTypes.string,
  // Parent-owned cache accessors (survive TabPanel unmount on tab switch) —
  // same contract as DocumentTab's getCached/setCached.
  getCachedReview: PropTypes.func,
  setCachedReview: PropTypes.func,
  // Clicking an item's id chip navigates the tree selection to it (spec
  // §Breakdown rendering "deep-link") — optional; a no-op if not supplied.
  onNavigateToItem: PropTypes.func,
  // N-21 / REQ-6133 (#609): bumped by the caller once per OBSERVED review-state
  // change (see useReviewActivity) — forces a cache-bypassing refetch. Optional;
  // omitted entirely, this tab simply has no live refresh (its pre-#609 behaviour).
  reviewChangeCount: PropTypes.number,
};

export default BreakdownTab;
